import { getWhatsAppUrl } from "@/lib/contact";
import { contactSection } from "@/i18n/contactSection";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { Store, Truck, ArrowRight, X } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import SectionHeading from "@/components/SectionHeading";
import { projectText } from "@/lib/project";
import { imagekitWidth } from "@/lib/imagekit";
import { API_BASE_URL } from "@/lib/apiBase";
import { mediaUrl } from "@/lib/mediaUrl";

const PROJECT_ICONS = { store: Store, delivery: Truck };

const PROJECT_THEMES = {
  charcoal: { panel: "bg-charcoal", iconWrap: "bg-white/10 text-mist", label: "text-softwhite", dots: "bg-dots-light", invert: true },
  sage: { panel: "bg-sage", iconWrap: "bg-white/15 text-softwhite", label: "text-softwhite", dots: "bg-dots-light", invert: true },
};

const ProjectVisual = ({ project, titleId, index }) => {
  const theme = PROJECT_THEMES[project.theme] || PROJECT_THEMES.charcoal;
  const Icon = PROJECT_ICONS[project.icon] || Store;
  const cover = project.cover_url || project.gallery?.[0];
  return (
    <div className={`project-visual relative h-64 overflow-hidden rounded-[1.4rem] md:h-80 ${theme.panel}`} aria-hidden="true">
      {cover && <img src={imagekitWidth(cover, 1000)} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />}
      {!cover && <>
      <div className={`absolute inset-0 opacity-55 ${theme.dots}`} />
      <div className="absolute inset-0 bg-[linear-gradient(120deg,transparent_25%,rgba(255,255,255,0.08)_48%,transparent_72%)]" />
      <img
                src={mediaUrl("/brand/logo-mark.png")}
        alt=""
        className={`absolute -bottom-16 -end-12 w-56 opacity-[0.07] ${theme.invert ? "invert" : ""}`}
      />
      <span className="project-orbit project-orbit-outer absolute left-1/2 top-1/2 size-52 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/10 md:size-64" />
      <span className="project-orbit project-orbit-inner absolute left-1/2 top-1/2 size-32 -translate-x-1/2 -translate-y-1/2 rounded-full border border-dashed border-white/15 md:size-40" />
      <div className="absolute inset-0 flex items-center justify-center">
        <span className={`project-icon flex size-20 items-center justify-center rounded-[1.75rem] border border-white/10 backdrop-blur-md ${theme.iconWrap}`}>
          <Icon className="size-8" strokeWidth={1.35} />
        </span>
      </div>
      </>}
      {cover && <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />}
      <div className="absolute inset-x-5 bottom-5 flex items-end justify-between gap-4">
        <span className={`font-display text-xl font-bold opacity-90 ${theme.label}`} id={titleId}>{project.latin || project.title_en}</span>
        <span className={`text-xs font-semibold tabular-nums opacity-45 ${theme.label}`} dir="ltr">0{index + 1}</span>
      </div>
    </div>
  );
};

const StatCounter = ({ count, approximate, lang, reduce }) => {
  const ref = useRef(null);
  const [current, setCurrent] = useState(reduce ? count : 0);
  const formatter = new Intl.NumberFormat(lang === "fr" ? "fr-FR" : "en-US");
  const prefix = approximate ? "≈ " : "";

  useEffect(() => {
    if (reduce || typeof IntersectionObserver === "undefined") {
      setCurrent(count);
      return undefined;
    }

    let frame;
    let started = false;
    let startTime;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting || started) return;
      started = true;
      observer.disconnect();
      const tick = (time) => {
        if (startTime === undefined) startTime = time;
        const progress = Math.min((time - startTime) / 1500, 1);
        setCurrent(Math.round(count * (1 - (1 - progress) ** 3)));
        if (progress < 1) frame = requestAnimationFrame(tick);
      };
      frame = requestAnimationFrame(tick);
    }, { threshold: 0.35 });

    if (ref.current) observer.observe(ref.current);
    return () => {
      observer.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [count, reduce]);

  return <strong ref={ref} className="block text-center text-lg font-bold tabular-nums text-charcoal sm:text-xl" aria-label={`${prefix}${formatter.format(count)}`}>
    <span dir="ltr" aria-hidden="true">{prefix}{formatter.format(current)}</span>
  </strong>;
};

const SelectedWork = () => {
  const { t, lang } = useLanguage();
  const reduce = useReducedMotion();
  const [remote, setRemote] = useState(null);
  const [activeGallery, setActiveGallery] = useState(null);

  useEffect(() => {
    if (!activeGallery) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setActiveGallery(null);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [activeGallery]);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE_URL}/api/projects`)
      .then((response) => response.ok ? response.json() : Promise.reject(new Error("failed")))
      .then((data) => { if (!cancelled) setRemote(Array.isArray(data.projects) ? data.projects : []); })
      .catch(() => { if (!cancelled) setRemote([]); });
    return () => { cancelled = true; };
  }, []);

  const hasRemote = Array.isArray(remote) && remote.length > 0;
  const remoteProjects = hasRemote
    ? remote.map((project) => ({ ...project, title: projectText(project, "title", lang), type: projectText(project, "type", lang), statement: projectText(project, "statement", lang) }))
    : [];
  const remoteAlSaree = remoteProjects.find((project) => project.slug === "al-saree");
  const projects = hasRemote
    ? [
        t.work.projects[0],
        remoteAlSaree ? {
          ...t.work.projects[1],
          ...remoteAlSaree,
          cover_url: t.work.projects[1].cover_url,
          gallery: t.work.projects[1].gallery,
          caseStudy: t.work.projects[1].caseStudy,
          galleryOnly: true,
        } : t.work.projects[1],
        ...remoteProjects.filter((project) => project.slug !== "sadeed" && project.slug !== "al-saree"),
      ]
    : t.work.projects;

  const openCaseStudy = (project) => setActiveGallery({
    title: project.title,
    images: [...new Set((project.gallery || []).filter(Boolean))],
    caseStudy: project.caseStudy,
    intro: project.galleryIntro,
    highlights: project.galleryHighlights,
  });

  const enter = (delay) => ({
    initial: reduce ? false : { opacity: 0, y: 26 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-70px" },
    transition: { duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] },
  });

  return (
    <section id="work" data-testid="selected-work-section" className="scroll-mt-28 py-20 md:py-32">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <SectionHeading
          eyebrow={t.work.eyebrow}
          title={t.work.title}
          sub={t.work.sub}
          testId="work-heading"
        />

        <div className="mt-14 grid gap-6 md:grid-cols-2">
          {projects.map((project, i) => (
            <motion.article
              key={project.id || project.title}
              {...enter(i * 0.08)}
              data-testid={`work-card-${i}`}
              className="interactive-card group flex flex-col rounded-[1.9rem] border border-line bg-white p-3.5 shadow-card"
            >
              {project.galleryOnly ? <button
                type="button"
                onClick={() => openCaseStudy(project)}
                aria-label={`${t.work.viewDetails}: ${project.title}`}
                className="block w-full rounded-[1.4rem] text-start focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-sage-deep"
              ><ProjectVisual project={project} titleId={`work-card-title-${i}`} index={i} /></button> : <ProjectVisual project={project} titleId={`work-card-title-${i}`} index={i} />}
              <div className="flex flex-1 flex-col px-3 pb-2 pt-5">
                <span className="inline-flex w-fit items-center rounded-full bg-mist px-3 py-1 text-xs font-semibold text-graphite">
                  {project.type}
                </span>
                <h3 className="mt-3.5 text-xl font-bold text-charcoal">{project.title}</h3>
                <p className="mt-2.5 flex-1 text-sm leading-relaxed text-graphite/70">
                  {project.statement}
                </p>
                {project.stats?.length > 0 && <div className="mt-5 border-t border-line pt-4">
                  <div className="grid grid-cols-3 gap-2">
                    {project.stats.map((stat) => <div key={stat.label} className="min-w-0 rounded-2xl bg-mist/55 px-2 py-3 text-center">
                      <StatCounter count={stat.count} approximate={stat.approximate} lang={lang} reduce={reduce} />
                      <span className="mt-1 block text-xs leading-5 text-graphite/70">{stat.label}</span>
                    </div>)}
                  </div>
                  <p className="mt-3 text-center text-[11px] text-graphite/55">{project.statsDate}</p>
                </div>}
                {project.galleryOnly ? <button
                  type="button"
                  onClick={() => openCaseStudy(project)}
                  data-testid={`work-card-cta-${i}`}
                  className="mt-5 inline-flex w-fit items-center gap-2 text-sm font-semibold text-sage-deep transition-colors duration-200 hover:text-charcoal"
                >
                  {t.work.viewDetails}
                  <ArrowRight className="size-4 transition-transform duration-300 group-hover:-translate-x-0.5 rtl:-scale-x-100 rtl:group-hover:translate-x-0.5" />
                </button> : project.slug ? <Link
                  to={`/projects/${project.slug}`}
                  data-testid={`work-card-cta-${i}`}
                  className="mt-5 inline-flex w-fit items-center gap-2 text-sm font-semibold text-sage-deep transition-colors duration-200 hover:text-charcoal"
                >
                  {t.work.viewProject}
                  <ArrowRight className="size-4 transition-transform duration-300 group-hover:-translate-x-0.5 rtl:-scale-x-100 rtl:group-hover:translate-x-0.5" />
                </Link> : <a
                  href={getWhatsAppUrl(contactSection[lang].whatsappMessage)}
                  target="_blank" rel="noopener noreferrer"
                  data-testid={`work-card-cta-${i}`}
                  className="mt-5 inline-flex w-fit items-center gap-2 text-sm font-semibold text-sage-deep transition-colors duration-200 hover:text-charcoal"
                >
                  {t.work.askCta}
                  <ArrowRight className="size-4 transition-transform duration-300 group-hover:-translate-x-0.5 rtl:-scale-x-100 rtl:group-hover:translate-x-0.5" />
                </a>}
              </div>
            </motion.article>
          ))}
        </div>

      </div>
      {activeGallery && <div className="fixed inset-0 z-50 flex items-center justify-center bg-charcoal/75 p-3 backdrop-blur-sm sm:p-6" onMouseDown={(event) => { if (event.target === event.currentTarget) setActiveGallery(null); }}>
        <div role="dialog" aria-modal="true" aria-label={`${t.work.viewDetails}: ${activeGallery.title}`} className="max-h-[92vh] w-full max-w-6xl overflow-y-auto rounded-3xl bg-softwhite p-4 shadow-soft sm:p-6 md:p-8">
          <div className="mb-5 flex items-center justify-between gap-4">
            <h2 className="font-display text-xl font-bold text-charcoal sm:text-2xl">{activeGallery.title}</h2>
            <button type="button" autoFocus onClick={() => setActiveGallery(null)} aria-label={t.work.closeDetails} className="inline-flex size-10 shrink-0 items-center justify-center rounded-full border border-line bg-white text-charcoal hover:bg-mist"><X className="size-5" /></button>
          </div>
          {(activeGallery.caseStudy?.intro || activeGallery.intro) && <p className="max-w-4xl text-sm leading-8 text-graphite/75 sm:text-base">{activeGallery.caseStudy?.intro || activeGallery.intro}</p>}
          {activeGallery.caseStudy && <section className="mt-9" aria-labelledby="al-saree-workflow-title">
            <h3 id="al-saree-workflow-title" className="font-display text-2xl font-bold text-charcoal">{activeGallery.caseStudy.heading}</h3>
            <ol className="mt-5 grid gap-4 md:grid-cols-2">
              {activeGallery.caseStudy.steps.map((step, index) => <li key={step.title} className="rounded-3xl border border-line bg-white p-5 sm:p-6">
                <div className="flex items-center gap-3">
                  <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-sage text-sm font-bold text-white" dir="ltr">{String(index + 1).padStart(2, "0")}</span>
                  <h4 className="font-display text-lg font-bold text-charcoal">{step.title}</h4>
                </div>
                <div className="mt-5 space-y-4 text-sm leading-7">
                  <div><p className="font-semibold text-graphite/60">{activeGallery.caseStudy.riskLabel}</p><p className="mt-1 text-graphite/75">{step.risk}</p></div>
                  <div className="rounded-2xl bg-mist/60 p-4"><p className="font-semibold text-sage-deep">{activeGallery.caseStudy.solutionLabel}</p><p className="mt-1 text-graphite/85">{step.solution}</p></div>
                </div>
              </li>)}
            </ol>
            <p className="mt-5 rounded-2xl bg-sage p-5 text-sm font-medium leading-8 text-white sm:text-base">{activeGallery.caseStudy.conclusion}</p>
          </section>}
          {activeGallery.images.length > 0 && <section className="mt-10 border-t border-line pt-7">
            <h3 className="font-display text-2xl font-bold text-charcoal">{activeGallery.caseStudy?.screenshotHeading || t.work.viewScreenshots}</h3>
            <div className="mt-5 space-y-6">
              {activeGallery.images.map((url, index) => <figure key={`${url}-${index}`} className="overflow-hidden rounded-3xl border border-line bg-white">
                <img src={imagekitWidth(url, 1600)} alt={activeGallery.caseStudy?.captions?.[index] || `${activeGallery.title} — ${index + 1}`} loading="lazy" className="block h-auto w-full" />
                {activeGallery.caseStudy?.captions?.[index] && <figcaption className="border-t border-line px-5 py-4 text-sm leading-7 text-graphite/75">{activeGallery.caseStudy.captions[index]}</figcaption>}
              </figure>)}
            </div>
          </section>}
          {!activeGallery.caseStudy && activeGallery.highlights?.length > 0 && <section className="mt-8 border-t border-line pt-6">
            <h3 className="font-display text-lg font-bold text-charcoal">{t.work.galleryDetails}</h3>
            <ul className="mt-4 grid gap-3 sm:grid-cols-2">
              {activeGallery.highlights.map((item, index) => <li key={index} className="rounded-2xl border border-line bg-white p-4 text-sm leading-7 text-graphite/80">{item}</li>)}
            </ul>
          </section>}
        </div>
      </div>}
    </section>
  );
};

export default SelectedWork;
